import { Check, Pencil, X } from "lucide-react";
import { DynamicIcon } from "lucide-react/dynamic";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { resolveChatIcon } from "@/lib/chat-icon";

/** Delay between typed characters for chat title animations. */
const TITLE_TYPING_DELAY_MS = 30;

/** Duration of the icon fade-swap when a new icon arrives. */
const ICON_FADE_DURATION_MS = 200;

/** Imperative handle exposed by ChatTitle via ref. */
export interface ChatTitleHandle {
  /** Trigger a character-by-character typing animation for a new title. */
  animateTitle: (newTitle: string) => void;
  /** Trigger a fade-swap animation when a new icon arrives. */
  animateIcon: (newIcon: string) => void;
}

interface ChatTitleProps {
  /** Base title from prop/API (e.g. "New Chat" or a loaded chat title). */
  title: string;
  /** Lucide icon name (kebab-case) to render alongside the title. */
  icon?: string | null;
  /** Team name to display as a badge next to the title. */
  teamName?: string | null;
  /** Whether the title can be edited inline from the header. */
  isEditable?: boolean;
  /** Whether a title update request is currently in-flight. */
  isSaving?: boolean;
  /** Persists the updated title after the user confirms the edit. */
  onSaveTitle?: (newTitle: string) => Promise<void> | void;
}

/**
 * Chat header title with an optional typing animation.
 * All animation state is encapsulated — the parent triggers it via `ref.current.animateTitle()`.
 */
export const ChatTitle = forwardRef<ChatTitleHandle, ChatTitleProps>(
  function ChatTitle(
    {
      title,
      icon,
      teamName,
      isEditable = false,
      isSaving = false,
      onSaveTitle,
    },
    ref,
  ) {
    /** Title currently rendered (static text once animation finishes). */
    const [displayedTitle, setDisplayedTitle] = useState(title);

    /** Title being animated — null when idle. */
    const [animatingTitle, setAnimatingTitle] = useState<string | null>(null);

    /** Number of characters revealed so far during animation. */
    const [visibleChars, setVisibleChars] = useState(0);

    /** Whether the header is currently showing the inline title editor. */
    const [isEditingTitle, setIsEditingTitle] = useState(false);

    /** Draft value used while the user edits the current title. */
    const [draftTitle, setDraftTitle] = useState(title);

    /** Input ref so edit mode can focus and select the current title immediately. */
    const titleInputRef = useRef<HTMLInputElement>(null);

    /**
     * Currently rendered icon name. Validated against the lucide registry
     * inline so an unknown name from the model degrades to the default.
     */
    const [currentIconName, setCurrentIconName] = useState<string>(() =>
      resolveChatIcon(icon),
    );

    /** Drives the opacity transition on icon swap. */
    const [iconFading, setIconFading] = useState(false);

    /** Expose imperative animation triggers to the parent. */
    useImperativeHandle(ref, () => ({
      animateTitle(newTitle: string) {
        setAnimatingTitle(newTitle);
        setVisibleChars(0);
      },
      animateIcon(newIcon: string) {
        const resolved = resolveChatIcon(newIcon);
        if (resolved === currentIconName) return;
        // Fade out, swap, fade in.
        setIconFading(true);
        window.setTimeout(() => {
          setCurrentIconName(resolved);
          setIconFading(false);
        }, ICON_FADE_DURATION_MS);
      },
    }));

    /** Sync current icon when the prop changes (e.g. a different chat is loaded). */
    useEffect(() => {
      setCurrentIconName(resolveChatIcon(icon));
    }, [icon]);

    /** Typing animation — reveals one character every 30 ms. */
    useEffect(() => {
      if (animatingTitle && visibleChars < animatingTitle.length) {
        const timer = setTimeout(() => {
          setVisibleChars((prev) => prev + 1);
        }, TITLE_TYPING_DELAY_MS);
        return () => clearTimeout(timer);
      } else if (animatingTitle && visibleChars >= animatingTitle.length) {
        setDisplayedTitle(animatingTitle);
        setAnimatingTitle(null);
      }
    }, [animatingTitle, visibleChars]);

    /** Sync displayed title when the prop changes outside of typing animation. */
    useEffect(() => {
      if (!animatingTitle) {
        setDisplayedTitle(title);
      }
    }, [animatingTitle, title]);

    /** Keep the edit draft aligned with the latest saved title when not editing. */
    useEffect(() => {
      if (!isEditingTitle) {
        setDraftTitle(title);
      }
    }, [isEditingTitle, title]);

    /** Focus the inline editor immediately so renaming feels direct. */
    useEffect(() => {
      if (!isEditingTitle) return;

      titleInputRef.current?.focus();
      titleInputRef.current?.select();
    }, [isEditingTitle]);

    /**
     * Opens edit mode with the current title pre-filled.
     * This keeps the interaction one click away from the header text.
     */
    function handleStartEditing() {
      if (!isEditable || animatingTitle) {
        return;
      }

      setDraftTitle(displayedTitle);
      setIsEditingTitle(true);
    }

    /**
     * Closes edit mode and restores the latest saved title.
     * This avoids keeping stale draft state after cancel.
     */
    function handleCancelEditing() {
      setDraftTitle(displayedTitle);
      setIsEditingTitle(false);
    }

    /**
     * Persists the renamed title when the draft is valid and changed.
     * The parent decides how the update is stored or surfaced to other views.
     */
    async function handleSaveEditing() {
      const nextTitle = draftTitle.trim();

      if (!nextTitle || nextTitle === displayedTitle) {
        setIsEditingTitle(false);
        return;
      }

      try {
        await onSaveTitle?.(nextTitle);
        setIsEditingTitle(false);
      } catch {
        // The parent surfaces the error toast; keep edit mode open for retry.
      }
    }

    const isSaveDisabled =
      isSaving
      || draftTitle.trim().length === 0
      || draftTitle.trim() === displayedTitle;

    return (
      <div className="flex-1 min-w-0 overflow-hidden">
        {isEditingTitle ? (
          <form
            className="flex min-w-0 flex-wrap items-center gap-1.5"
            onSubmit={(event) => {
              event.preventDefault();
              void handleSaveEditing();
            }}
          >
            <Input
              ref={titleInputRef}
              value={draftTitle}
              onChange={(event) => setDraftTitle(event.target.value)}
              maxLength={255}
              className="h-auto min-w-[180px] max-w-xl flex-1 border-0 bg-transparent px-0 py-0 text-lg font-semibold shadow-none focus-visible:ring-0"
              placeholder="Enter chat title"
              disabled={isSaving}
              onKeyDown={(event) => {
                if (event.key === "Escape") {
                  event.preventDefault();
                  handleCancelEditing();
                }
              }}
            />

            <Button
              type="submit"
              variant="ghost"
              size="icon-sm"
              className="size-7 rounded-md text-primary hover:bg-primary/10 hover:text-primary"
              disabled={isSaveDisabled}
              title="Save title"
              aria-label="Save title"
            >
              <Check className="h-4 w-4" />
            </Button>

            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="size-7 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={handleCancelEditing}
              disabled={isSaving}
              title="Cancel rename"
              aria-label="Cancel rename"
            >
              <X className="h-4 w-4" />
            </Button>

            {teamName && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                #{teamName}
              </span>
            )}
          </form>
        ) : (
          <h1 className="flex min-w-0 items-center gap-2 overflow-hidden text-lg font-semibold text-foreground">
            <DynamicIcon
              name={currentIconName as Parameters<typeof DynamicIcon>[0]["name"]}
              className={`size-5 shrink-0 text-muted-foreground transition-opacity duration-200 ${iconFading ? "opacity-0" : "opacity-100"}`}
              aria-hidden
            />
            {isEditable ? (
              <div className="group flex min-w-0 items-center gap-1">
                <span className="truncate min-w-0">
                  {animatingTitle ? (
                    <>
                      {animatingTitle.slice(0, visibleChars)}
                      <span className="animate-pulse">|</span>
                    </>
                  ) : (
                    displayedTitle
                  )}
                </span>
                <button
                  type="button"
                  onClick={handleStartEditing}
                  className="rounded-md p-1 text-muted-foreground opacity-0 transition-all hover:bg-muted hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover:opacity-100"
                  title="Rename chat"
                  aria-label="Rename chat"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              </div>
            ) : (
              <span className="truncate min-w-0">
                {animatingTitle ? (
                  <>
                    {animatingTitle.slice(0, visibleChars)}
                    <span className="animate-pulse">|</span>
                  </>
                ) : (
                  displayedTitle
                )}
              </span>
            )}

            {teamName && (
              <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                #{teamName}
              </span>
            )}
          </h1>
        )}
      </div>
    );
  },
);
